import { useState } from 'react';

// Sentry
import * as Sentry from '@sentry/react';

// Compression
import {
  compressSessionStorage,
  decompressSessionStorage,
} from '../utils/sessionStorage';
import i18n from '../i18n';
import { clearOfflineSession, saveOfflineSession } from '../utils/offlineSession';
import { AuthType } from '../types/auth';

export const useSessionStorage = (keyName: string) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const value = sessionStorage.getItem(keyName);
      if (value) {
        return JSON.parse(decompressSessionStorage(value));
      }
      return null;
    } catch (err) {
      Sentry.captureException(err);
      console.log('[sesión] sessionStorage no está disponible');
    }
  });

  const setValue = (newValue: unknown) => {
    try {
      if (newValue === null) {
        sessionStorage.removeItem(keyName);
        // Este hook es genérico, pero la clave `user` es la sesión: por aquí
        // pasa el logout del menú (`useAuth.setUser(null)`), que NO usa
        // `setSessionStorageUser`. Sin esta línea la copia para reapertura sin
        // cobertura sobreviviría al cierre de sesión en `localStorage`, y el
        // siguiente operario de una tablet compartida podría reabrir sin red
        // con la sesión del anterior.
        if (keyName === 'user') clearOfflineSession();
      } else {
        sessionStorage.setItem(
          keyName,
          compressSessionStorage(JSON.stringify(newValue))
        );
        // Simétrico al borrado de arriba: por aquí pasa el LOGIN normal
        // (`useAuth.setUser(authPayload)` desde Login/action.ts), que tampoco
        // usa `setSessionStorageUser`. Sin esta línea la copia para reapertura
        // sin cobertura no llegaba a escribirse nunca — cazado en la prueba de
        // navegador del escenario del túnel, no por los tests unitarios.
        if (keyName === 'user') saveOfflineSession(newValue as AuthType);
      }
      // Lo propio y lo ajeno de las colas dependen de quién tiene la sesión:
      // el banner recuenta y, si vuelve el dueño, lo suyo se envía solo
      // (spec 2026-10-01 § 6.3).
      if (keyName === 'user') window.dispatchEvent(new CustomEvent('session-changed'));
    } catch (err) {
      Sentry.captureException(err);
      console.log('[sesión] sessionStorage no está disponible');
    }
    setStoredValue(newValue);
  };

  return [storedValue, setValue];
};
